import React, { useState, useEffect } from 'react'
import { Card } from './ui/card'
import { Button } from './ui/button'
import { ExternalLink, AlertCircle, Play, Copy, Check } from 'lucide-react'
import { toast } from 'sonner'

const platformBackgrounds = {
  facebook: 'linear-gradient(135deg, #1877f2 0%, #0d5fc7 100%)',
  youtube: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
  instagram: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  tiktok: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
  reddit: 'linear-gradient(135deg, #ff4500 0%, #ff5722 100%)',
  linkedin: 'linear-gradient(135deg, #0077b5 0%, #005885 100%)',
  twitter: 'linear-gradient(135deg, #1da1f2 0%, #0d8bd9 100%)'
}

const platformIcons = {
  facebook: '📘',
  youtube: '▶️',
  instagram: '📷',
  tiktok: '🎵',
  reddit: '🔴',
  linkedin: '💼',
  twitter: '🐦'
}

const platformNames = {
  facebook: 'Facebook',
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)'
}

// Extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

// Get thumbnail URL based on platform
const getThumbnailUrl = (url, platform) => {
  if (platform === 'youtube') {
    const videoId = getYouTubeVideoId(url)
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    }
  }
  return null
}

export default function LinkPreviewCard({ url, platform }) {
  const [linkBroken, setLinkBroken] = useState(false)
  const [copied, setCopied] = useState(false)
  const [thumbnailUrl, setThumbnailUrl] = useState(null)
  const [thumbnailError, setThumbnailError] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  useEffect(() => {
    // Try to get thumbnail
    const thumb = getThumbnailUrl(url, platform)
    if (thumb) {
      setThumbnailUrl(thumb)
    }
  }, [url, platform])
  
  const openLink = (e) => {
    e.stopPropagation()
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    if (!opened) {
      setLinkBroken(true)
    }
  }
  
  const copyToClipboard = async (e) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      toast.error('Failed to copy link')
    }
  }
  
  const icon = platformIcons[platform] || '🔗'
  const name = platformNames[platform] || platform
  const bgStyle = platformBackgrounds[platform] || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
  const hasThumbnail = thumbnailUrl && !thumbnailError
  
  // Desktop view - NO play button overlay, just thumbnail/icon with copy URL below
  if (!isMobile) {
    return (
      <Card className="overflow-hidden">
        {/* Preview Image Area - clickable to open link */}
        <div 
          className="relative h-40 overflow-hidden cursor-pointer"
          style={{ background: hasThumbnail ? '#000' : bgStyle }}
          onClick={openLink}
        >
          {/* Thumbnail or Platform Icon */}
          {hasThumbnail ? (
            <img 
              src={thumbnailUrl}
              alt={`${name} preview`}
              className="w-full h-full object-cover hover:opacity-80 transition-opacity"
              onError={() => setThumbnailError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-6xl opacity-50">{icon}</span>
            </div>
          )}
          
          {/* Link broken indicator */}
          {linkBroken && (
            <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1 z-20">
              <AlertCircle className="w-3 h-3" />
              May be unavailable
            </div>
          )}
        </div>
        
        {/* URL Info with Copy */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{icon}</span>
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
              {name}
            </span>
          </div>
          
          {/* Copyable URL */}
          <div className="flex items-center gap-2 bg-white dark:bg-gray-700 p-2 rounded border dark:border-gray-600">
            <input
              type="text"
              value={url}
              readOnly
              className="flex-1 text-xs text-gray-600 dark:text-gray-300 bg-transparent outline-none truncate"
              onClick={(e) => e.target.select()}
            />
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={copyToClipboard}
              className="h-7 px-2"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
            Copy and paste the URL in your browser to see the post
          </p>
        </div>
      </Card>
    )
  }
  
  // Mobile view - tap to open with centered play button
  return (
    <Card className="overflow-hidden cursor-pointer relative" onClick={openLink}>
      {/* Preview Image Area */}
      <div 
        className="relative h-32 overflow-hidden"
        style={{ background: hasThumbnail ? '#000' : bgStyle }}
      >
        {/* Thumbnail or Platform Icon */}
        {hasThumbnail ? (
          <img 
            src={thumbnailUrl}
            alt={`${name} preview`}
            className="w-full h-full object-cover opacity-80"
            onError={() => setThumbnailError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl opacity-30">{icon}</span>
          </div>
        )}
        
        {/* Always visible centered button - MOBILE ONLY - contained within card */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <Button
            size="sm"
            className="bg-white/95 hover:bg-white text-gray-800 font-semibold shadow-xl"
            onClick={openLink}
          >
            <Play className="w-4 h-4 mr-2 fill-current" />
            View on {name}
          </Button>
        </div>
        
        {/* Link broken indicator */}
        {linkBroken && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Unavailable
          </div>
        )}
      </div>
      
      {/* URL Info */}
      <div className="p-2 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
            {name}
          </span>
          <ExternalLink className="w-3 h-3 text-gray-400 ml-auto" />
        </div>
      </div>
    </Card>
  )
}
